/**
 * Step references in judge reasoning — "step-5", "step 5", "(step-11, …)",
 * "steps 3, 4, 9, 12", "steps 9 and 10", "steps 3–5". Each cited step
 * number becomes its own segment so the reader can open that step's
 * evidence; a range ("steps 2-5", "steps 3 to 7") stays one segment that
 * opens its first step. Everything else stays text.
 */
export type StepRefSegment =
  | { kind: "text"; text: string }
  | {
      kind: "step";
      text: string;
      stepNumber: number;
      /** Last step of a range ("steps 2-5" → 5); absent for one step. */
      endStepNumber?: number;
    };

// "step" / "steps", an optional space, hyphen or "#", then a number,
// then any run of ", 4", ", and 9", " and 10", " & 3", "–5", " to 7".
const STEP_REF =
  /\bsteps?[\s-]?#?\d+(?:(?:\s*,\s*(?:and\s+|&\s*)?|\s+(?:and|to)\s+|\s*[&–—-]\s*)\d+)*\b/gi;
const NUMBER = /\d+/g;
// The separator between two numbers that makes them a range.
const RANGE_SEPARATOR = /^\s*(?:[–—-]|to)\s*$/i;

export function splitStepRefs(text: string): StepRefSegment[] {
  const segments: StepRefSegment[] = [];
  let last = 0;
  const pushText = (t: string) => {
    if (!t) return;
    const prev = segments[segments.length - 1];
    if (prev?.kind === "text") prev.text += t;
    else segments.push({ kind: "text", text: t });
  };

  for (const match of text.matchAll(STEP_REF)) {
    const start = match.index;
    pushText(text.slice(last, start));
    const ref = match[0];
    let cursor = 0;
    let first = true;
    for (const n of ref.matchAll(NUMBER)) {
      const end = n.index + n[0].length;
      // The first number carries its "step"/"steps" prefix ("step-5");
      // later numbers in a list stand alone, their separators stay text.
      if (first) {
        segments.push({
          kind: "step",
          text: ref.slice(0, end),
          stepNumber: Number(n[0]),
        });
        first = false;
      } else {
        const separator = ref.slice(cursor, n.index);
        const prev = segments[segments.length - 1];
        if (
          prev?.kind === "step" &&
          prev.endStepNumber === undefined &&
          RANGE_SEPARATOR.test(separator)
        ) {
          // "steps 2-5": one reference to the span, opening its start.
          prev.text += separator + n[0];
          prev.endStepNumber = Number(n[0]);
        } else {
          pushText(separator);
          segments.push({
            kind: "step",
            text: n[0],
            stepNumber: Number(n[0]),
          });
        }
      }
      cursor = end;
    }
    pushText(ref.slice(cursor));
    last = start + ref.length;
  }
  pushText(text.slice(last));
  return segments;
}
